/**
 * TicketHall — Programação: client tipado (core).
 * Escrita crítica via RPCs; leitura pública por contrato explícito.
 * Tabelas/RPCs presentes em database.types (mescla cirúrgica pós-migration).
 * `as any` mantido nos retornos RPC (Returns Json) por simplicidade.
 */
import { supabase } from "./supabase-client";
import type {
  ScheduleAnnouncement,
  ScheduleDay,
  ScheduleEntityType,
  ScheduleError,
  ScheduleOperationKind,
  ScheduleQuestion,
  ScheduleRatingSummary,
  ScheduleReservation,
  ScheduleRevision,
  ScheduleRoom,
  ScheduleRoom as Room,
  ScheduleSettings,
  ScheduleSession,
  ScheduleSpeaker,
  ScheduleTrack,
  ScheduleCategory,
  ScheduleRuntime,
  PublicScheduleData, ScheduleParticipation, SchedulePublicMaterial,
} from "./schedule/types";

const db = () => supabase as any;

type Ok<T> = { data: T; error: null };
type Fail = { data: null; error: ScheduleError };
type Res<T> = Promise<Ok<T> | Fail>;

const fail = (code: ScheduleError["code"], message: string, fields?: string[]): Fail => ({
  data: null,
  error: { code, message, fields },
});

function mapError(e: any): ScheduleError {
  const msg = String(e?.message ?? "Erro inesperado.");
  if (/VERSION_CONFLICT|version/i.test(msg)) return { code: "VERSION_CONFLICT", message: msg };
  if (/SCHEDULE_CONFLICT/i.test(msg)) return { code: "SCHEDULE_CONFLICT", message: "Este ingresso já tem uma reserva no mesmo horário." };
  if (/TICKET_NOT_ELIGIBLE/i.test(msg)) return { code: "TICKET_NOT_ELIGIBLE", message: "Você precisa de um ingresso elegível desta conta." };
  if (/RESERVATION_CLOSED/i.test(msg)) return { code: "RESERVATION_CLOSED", message: "Esta ação não está aberta neste momento." };
  if (/RATE_LIMITED/i.test(msg)) return { code: "RATE_LIMITED", message: "Aguarde um minuto antes de tentar novamente." };
  if (/permission|policy|forbidden|RLS/i.test(msg)) return { code: "FORBIDDEN", message: msg };
  if (/capacity|lotad/i.test(msg)) return { code: "CAPACITY_REACHED", message: "As vagas acabaram. Atualize para consultar a disponibilidade." };
  return { code: "VALIDATION_FAILED", message: msg };
}

// ---------- Leituras ----------

export async function getScheduleEditor(eventId: string): Res<{
  settings: ScheduleSettings | null;
  days: ScheduleDay[];
  rooms: Room[];
  tracks: ScheduleTrack[];
  categories: ScheduleCategory[];
  sessions: ScheduleSession[];
  speakers: ScheduleSpeaker[];
}> {
  try {
    const c = db();
    const [settings, days, rooms, tracks, categories, sessions, speakers] = await Promise.all([
      c.from("event_schedule_settings").select("*").eq("event_id", eventId).maybeSingle(),
      c.from("event_schedule_days").select("*").eq("event_id", eventId).order("sort_order"),
      c.from("event_schedule_rooms").select("*").eq("event_id", eventId).order("sort_order"),
      c.from("event_schedule_tracks").select("*").eq("event_id", eventId).order("sort_order"),
      c.from("event_schedule_categories").select("*").eq("event_id", eventId).order("sort_order"),
      c.from("event_schedule_sessions").select("*, speakers:event_schedule_session_speakers(*)").eq("event_id", eventId).order("starts_at"),
      c.from("event_schedule_speakers").select("*").eq("event_id", eventId).order("public_name"),
    ]);
    for (const r of [settings, days, rooms, tracks, categories, sessions, speakers]) {
      if (r.error) return fail("NOT_FOUND", r.error.message);
    }
    return { data: { settings: settings.data, days: days.data ?? [], rooms: rooms.data ?? [], tracks: tracks.data ?? [], categories: categories.data ?? [], sessions: sessions.data ?? [], speakers: speakers.data ?? [] }, error: null };
  } catch (e) {
    return fail("NOT_FOUND", e instanceof Error ? e.message : "Falha ao carregar rascunho.");
  }
}

export async function getPublishedSchedule(eventId: string): Res<PublicScheduleData> {
  try {
    const { data, error } = await db().rpc("get_published_schedule", { p_event_id: eventId });
    if (error) return { data: null, error: mapError(error) };
    if (!data || typeof data.server_now !== "string") return fail("VALIDATION_FAILED", "Resposta da programação inválida.");
    return { data: { ...data, runtime: data.runtime ?? {}, announcements: data.announcements ?? [], received_at: Date.now() }, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getScheduleParticipation(sessionId: string): Res<ScheduleParticipation> {
  try {
    const { data, error } = await db().rpc("get_schedule_participation", { p_session_id: sessionId });
    if (error) throw error;
    return { data, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getSchedulePermissions(eventId: string): Res<{ canEdit: boolean; canOperate: boolean; canModerate: boolean; canPublish: boolean }> {
  try {
    const { data, error } = await db().rpc("get_schedule_permissions", { p_event_id: eventId });
    if (error) throw error;
    return { data, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getPublicScheduleMaterials(sessionId: string): Res<SchedulePublicMaterial[]> {
  try {
    const { data, error } = await db().rpc("get_schedule_materials", { p_session_id: sessionId });
    if (error) throw error;
    const rows = await Promise.all(((data ?? []) as SchedulePublicMaterial[]).map(async (m) => {
      if (!m.accessible || m.url || !m.bucket || !m.path) return m;
      const signed = await supabase.storage.from(m.bucket).createSignedUrl(m.path, 300);
      if (signed.error) throw signed.error;
      return { ...m, url: signed.data.signedUrl };
    }));
    return { data: rows, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getScheduleSession(eventId: string, sessionId: string): Res<{ session: ScheduleSession | null; runtime: ScheduleRuntime | null }> {
  try {
    const c = db();
    const [{ data: session, error: e1 }, { data: runtime, error: e2 }] = await Promise.all([
      c.from("event_schedule_sessions").select("*, speakers:event_schedule_session_speakers(*)").eq("event_id", eventId).eq("id", sessionId).maybeSingle(),
      c.from("event_schedule_runtime").select("*").eq("event_id", eventId).eq("session_id", sessionId).maybeSingle(),
    ]);
    if (e1) return fail("NOT_FOUND", e1.message);
    if (e2) return fail("NOT_FOUND", e2.message);
    if (!session) return fail("NOT_FOUND", "Atividade não encontrada.");
    return { data: { session, runtime }, error: null };
  } catch (e) {
    return fail("NOT_FOUND", e instanceof Error ? e.message : "Falha ao carregar atividade.");
  }
}

export async function getScheduleQuestions(sessionId: string, _cursor?: string, limit = 30, state?: string, offset = 0): Res<ScheduleQuestion[]> {
  try {
    const { data, error } = await db().rpc("get_schedule_questions", {
      p_session_id: sessionId, p_state: state ?? null, p_offset: offset, p_limit: limit,
    });
    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getMySchedule(eventId: string): Res<{ favorites: string[]; reservations: ScheduleReservation[] }> {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user?.user) return fail("FORBIDDEN", "Login necessário.");
    const c = db();
    const [{ data: favs, error: e1 }, { data: res, error: e2 }] = await Promise.all([
      c.from("event_schedule_favorites").select("session_id").eq("event_id", eventId).eq("user_id", user.user.id),
      c.from("event_schedule_reservations").select("*").eq("event_id", eventId).eq("user_id", user.user.id).eq("state", "active"),
    ]);
    if (e1) return fail("FORBIDDEN", e1.message);
    if (e2) return fail("FORBIDDEN", e2.message);
    return { data: { favorites: (favs ?? []).map((f: any) => f.session_id), reservations: res ?? [] }, error: null };
  } catch (e) {
    return fail("FORBIDDEN", e instanceof Error ? e.message : "Falha ao carregar agenda.");
  }
}

export async function getScheduleReservations(sessionId: string): Res<{ items: ScheduleReservation[]; capacity: number | null; confirmed: number }> {
  try {
    const { data, error } = await db().rpc("get_schedule_reservations", { p_session_id: sessionId });
    if (error) throw error;
    return { data, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

export async function getScheduleRatings(sessionId: string): Res<ScheduleRatingSummary> {
  try {
    const {data,error}=await db().rpc("get_schedule_ratings",{p_session_id:sessionId});
    if(error)throw error;
    return {data,error:null};
  } catch (e) {
    return fail("FORBIDDEN", e instanceof Error ? e.message : "Falha ao carregar avaliações.");
  }
}

export async function getScheduleHistory(eventId: string, limit = 50, cursor?: string): Res<any[]> {
  try {
    let q = db().from("event_schedule_audit_logs").select("*").eq("event_id", eventId).order("created_at", { ascending: false }).limit(limit);
    if (cursor) q = q.lt("created_at", cursor);
    const { data, error } = await q;
    if (error) return fail("FORBIDDEN", error.message);
    return { data: data ?? [], error: null };
  } catch (e) {
    return fail("FORBIDDEN", e instanceof Error ? e.message : "Falha ao carregar histórico.");
  }
}

export async function getScheduleRooms(eventId: string): Res<ScheduleRoom[]> {  try {
    const { data, error } = await db().from("event_schedule_rooms").select("*").eq("event_id", eventId).is("archived_at", null).order("sort_order");
    if (error) return fail("NOT_FOUND", error.message);
    return { data: data ?? [], error: null };
  } catch (e) {
    return fail("NOT_FOUND", e instanceof Error ? e.message : "Falha ao carregar salas.");
  }
}

// ---------- Mutações editoriais (via RPC com versionamento) ----------

export async function saveScheduleEntity(args: {
  entity: ScheduleEntityType;
  event_id: string;
  id?: string;
  payload: Record<string, unknown>;
  expected_version?: number;
}): Res<{ id: string }> {
  try {
    const { data, error } = await supabase.rpc("save_schedule_entity" as any, {
      p_entity: args.entity,
      p_event_id: args.event_id,
      p_id: args.id ?? null,
      p_payload: args.payload as any,
      p_expected_version: args.expected_version ?? null,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { id: string }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function archiveScheduleEntity(args: { entity: ScheduleEntityType; event_id: string; id: string }): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("archive_schedule_entity" as any, {
      p_entity: args.entity,
      p_event_id: args.event_id,
      p_id: args.id,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function validateSchedulePublication(eventId: string): Res<{ blockers: any[]; warnings: any[] }> {
  try {
    const { data, error } = await supabase.rpc("validate_schedule_publication" as any, { p_event_id: eventId } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { blockers: any[]; warnings: any[] }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function publishSchedule(eventId: string): Res<{ revision_number: number }> {
  try {
    const { data, error } = await supabase.rpc("publish_schedule" as any, { p_event_id: eventId } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { revision_number: number }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

// ---------- Operação ----------

export async function applyScheduleOperation(args: {
  event_id: string;
  session_id: string;
  kind: ScheduleOperationKind;
  idempotency_key: string;
  expected_version?: number;
  patch?: Record<string, unknown>;
}): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("apply_schedule_operation" as any, {
      p_event_id: args.event_id,
      p_session_id: args.session_id,
      p_kind: args.kind,
      p_key: args.idempotency_key,
      p_expected_version: args.expected_version ?? null,
      p_patch: (args.patch ?? {}) as any,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function saveScheduleAnnouncement(args: {
  event_id: string;
  id?: string;
  target: ScheduleAnnouncement["target"];
  room_id?: string | null;
  session_id?: string | null;
  message: string;
  starts_at?: string | null;
  expires_at: string;
}): Res<{ id: string }> {
  try {
    const { data, error } = await supabase.rpc("save_schedule_announcement" as any, {
      p_event_id: args.event_id,
      p_id: args.id ?? null,
      p_target: args.target,
      p_room_id: args.room_id ?? null,
      p_session_id: args.session_id ?? null,
      p_message: args.message,
      p_starts_at: args.starts_at ?? null,
      p_expires_at: args.expires_at,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { id: string }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

// ---------- Participante ----------

export async function setScheduleFavorite(sessionId: string, saved: boolean): Res<{ saved: boolean }> {
  try {
    const { data, error } = await supabase.rpc("set_schedule_favorite" as any, { p_session_id: sessionId, p_saved: saved } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { saved: boolean }) ?? { saved }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function reserveScheduleSession(args: { session_id: string; ticket_id: string }): Res<{ reservation_id: string }> {
  try {
    const { data, error } = await supabase.rpc("reserve_schedule_session" as any, {
      p_session_id: args.session_id,
      p_ticket_id: args.ticket_id,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { reservation_id: string }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function cancelScheduleReservation(reservationId: string, reason?: string): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("cancel_schedule_reservation" as any, {
      p_reservation_id: reservationId,
      p_reason: reason ?? null,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function replaceScheduleReservation(args: { from_reservation_id: string; to_session_id: string; ticket_id: string }): Res<{ reservation_id: string }> {
  try {
    const { data, error } = await supabase.rpc("replace_schedule_reservation" as any, {
      p_from_id: args.from_reservation_id,
      p_to_session: args.to_session_id,
      p_ticket_id: args.ticket_id,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { reservation_id: string }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function submitScheduleQuestion(args: { session_id: string; body: string; anonymous: boolean; author_name?: string | null }): Res<{ id: string; state: string }> {
  try {
    const { data, error } = await supabase.rpc("submit_schedule_question" as any, {
      p_session_id: args.session_id,
      p_body: args.body,
      p_anonymous: args.anonymous,
      p_author_name: args.author_name ?? null,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { id: string; state: string }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function moderateScheduleQuestion(args: {
  question_id: string;
  action: "approve" | "hide" | "pin" | "unpin" | "answer";
  reason?: string | null;
  response?: string | null;
}): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("moderate_schedule_question" as any, {
      p_question_id: args.question_id,
      p_action: args.action,
      p_reason: args.reason ?? null,
      p_response: args.response ?? null,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function setScheduleQuestionVote(questionId: string, voted: boolean): Res<{ votes: number }> {
  try {
    const { data, error } = await supabase.rpc("set_schedule_question_vote" as any, { p_question_id: questionId, p_voted: voted } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: data as { votes: number }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function setScheduleRating(sessionId: string, rating: number): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("set_schedule_rating" as any, { p_session_id: sessionId, p_rating: rating } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export async function manageScheduleAccess(args: { event_id: string; user_id: string; capability: "operate" | "moderate"; grant: boolean }): Res<{ ok: boolean }> {
  try {
    const { data, error } = await supabase.rpc("manage_schedule_access" as any, {
      p_event_id: args.event_id,
      p_user_id: args.user_id,
      p_capability: args.capability,
      p_grant: args.grant,
    } as any);
    if (error) return { data: null, error: mapError(error) };
    return { data: (data as { ok: boolean }) ?? { ok: true }, error: null };
  } catch (e) {
    return { data: null, error: mapError(e) };
  }
}

export type { ScheduleAnnouncement };

// ---------- Agenda do produtor ----------

/** Runtime de todas as sessões (modo "Durante o evento"). */
export async function getScheduleRuntimes(eventId: string): Res<Record<string, ScheduleRuntime>> {
  try {
    const { data, error } = await db().from("event_schedule_runtime").select("*").eq("event_id", eventId);
    if (error) return fail("FORBIDDEN", error.message);
    const map: Record<string, ScheduleRuntime> = {};
    for (const r of (data ?? []) as ScheduleRuntime[]) map[r.session_id] = r;
    return { data: map, error: null };
  } catch (e) {
    return fail("FORBIDDEN", e instanceof Error ? e.message : "Falha ao carregar operação.");
  }
}

/** Status de publicação: rascunho atual vs última revisão (nº real de alterações). */
export async function getSchedulePublishStatus(eventId: string): Res<{
  draft_version: number;
  published: { revision_number: number; draft_version: number; created_at: string } | null;
  pending: number;
}> {
  try {
    const c = db();
    const [{ data: settings, error: e1 }, { data: rev, error: e2 }] = await Promise.all([
      c.from("event_schedule_settings").select("draft_version").eq("event_id", eventId).maybeSingle(),
      c.from("event_schedule_revisions").select("revision_number,draft_version,created_at").eq("event_id", eventId).order("revision_number", { ascending: false }).limit(1).maybeSingle(),
    ]);
    if (e1) return fail("FORBIDDEN", e1.message);
    if (e2) return fail("FORBIDDEN", e2.message);
    const draft = (settings as { draft_version: number } | null)?.draft_version ?? 1;
    const published = (rev as { revision_number: number; draft_version: number; created_at: string } | null) ?? null;
    return { data: { draft_version: draft, published, pending: published ? Math.max(0, draft - published.draft_version) : 0 }, error: null };
  } catch (e) {
    return fail("FORBIDDEN", e instanceof Error ? e.message : "Falha ao carregar status.");
  }
}

export interface ScheduleMaterialLink {
  id: string;
  title: string;
  external_url: string;
  status: string;
  audience?: "public" | "participants";
  release_mode?: "immediate" | "after_end" | "scheduled";
  release_at?: string | null;
  event_schedule_session_assets: Array<{ session_id: string }>;
}

export async function getScheduleMaterialLinks(eventId: string): Res<ScheduleMaterialLink[]> {
  try {
    const { data, error } = await db().from("event_schedule_assets")
      .select("id,title,external_url,status,audience,release_mode,release_at,event_schedule_session_assets(session_id)")
      .eq("event_id", eventId).eq("kind", "link").neq("status", "retired").order("created_at");
    if (error) throw error;
    return { data: data ?? [], error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}

/** A stable draft id lets a failed association be retried without duplicating the link. */
export async function saveScheduleMaterialLink(args: { id: string; event_id: string; session_id: string; title: string; url: string }): Res<{ id: string }> {
  try {
    const url = new URL(args.url);
    if (url.protocol !== "https:" || url.username || url.password) return fail("VALIDATION_FAILED", "Use um link HTTPS sem credenciais.");
    if (!args.title.trim() || args.title.trim().length > 200) return fail("VALIDATION_FAILED", "Informe um título de até 200 caracteres.");
    const {error}=await db().rpc("save_schedule_material_link",{p_id:args.id,p_event_id:args.event_id,p_session_id:args.session_id,p_title:args.title.trim(),p_url:url.href});
    if(error)throw error;
    return { data: { id: args.id }, error: null };
  } catch (e) { return { data: null, error: mapError(e) }; }
}


export async function getScheduleStaffEvents(): Res<Array<{ id: string; title: string; start_date: string; end_date: string; can_operate:boolean }>> {
  try { const {data,error}=await db().rpc("get_schedule_staff_events"); if(error)throw error; return {data:data??[],error:null}; }
  catch(e){return {data:null,error:mapError(e)};}
}
export async function getSchedulePublicationReview(eventId: string): Res<{ previous: ScheduleRevision | null; event: { slug: string; status: string }; enabled: boolean }> {
  try {
    const c=db();
    const [settings,event]=await Promise.all([c.from("event_schedule_settings").select("enabled,published_revision_id").eq("event_id",eventId).maybeSingle(),c.from("events").select("slug,status").eq("id",eventId).single()]);
    if(settings.error)throw settings.error;if(event.error)throw event.error;
    let previous=null;
    if(settings.data?.published_revision_id){const r=await c.from("event_schedule_revisions").select("*").eq("id",settings.data.published_revision_id).single();if(r.error)throw r.error;previous=r.data;}
    return {data:{previous,event:event.data,enabled:settings.data?.enabled??false},error:null};
  }catch(e){return {data:null,error:mapError(e)};}
}
export async function updateScheduleMaterial(args: {event_id:string; id:string; status?:'draft'|'available'|'retired';audience?:'public'|'participants';release_mode?:'immediate'|'after_end'|'scheduled';release_at?:string|null}): Res<{ok:boolean}> {
  try{const {event_id,id,...patch}=args;const {data,error}=await db().from("event_schedule_assets").update(patch).eq("id",id).eq("event_id",event_id).select("id").single();if(error||!data)throw error??new Error("Material não encontrado.");return {data:{ok:true},error:null};}catch(e){return {data:null,error:mapError(e)};}
}

export async function getScheduleAnnouncements(eventId:string):Res<ScheduleAnnouncement[]>{try{const {data,error}=await db().from("event_schedule_announcements").select("*").eq("event_id",eventId).is("ended_at",null).gt("expires_at",new Date().toISOString()).order("starts_at",{ascending:false});if(error)throw error;return {data:data??[],error:null};}catch(e){return {data:null,error:mapError(e)};}}
export async function endScheduleAnnouncement(id:string):Res<{ok:boolean}>{try{const {data,error}=await db().rpc("end_schedule_announcement",{p_id:id});if(error)throw error;return {data,error:null};}catch(e){return {data:null,error:mapError(e)};}}

export async function getScheduleTeam(eventId:string):Res<Array<{id:string;name:string;operate:boolean;moderate:boolean}>> {try {const {data,error}=await db().rpc("get_schedule_team",{p_event_id:eventId});if(error)throw error;return {data:data??[],error:null};}catch(e){return {data:null,error:mapError(e)};}}
